<template>
    <van-dialog v-model:show="visible" :title="title" :width="width" :message="message" :message-align="messageAlign"
        :theme="theme" :className="className" :show-cancel-button="showCancelButton"
        :show-confirm-button="showConfirmButton" :cancel-button-text="cancelButtonText"
        :confirm-button-text="confirmButtonText" :cancel-button-color="cancelButtonColor"
        :confirm-button-color="confirmButtonColor" :overlay="overlay" :overlay-class="overlayClass"
        :overlay-style="overlayStyle" :close-on-click-overlay="closeOnClickOverlay" :close-on-popstate="closeOnPopstate"
        :lazy-render="lazyRender" :lock-scroll="lockScroll" :allow-html="allowHtml" :before-close="handleBeforeClose"
        @confirm="handleConfirm" @cancel="handleCancel" @open="handleOpen" @close="handleClose" @opened="handleOpened"
        @closed="handleClosed">
        <!-- 自定义标题 -->
        <template #title v-if="$slots.title">
            <slot name="title"></slot>
        </template>

        <!-- 自定义内容 -->
        <template #default v-if="$slots.default">
            <slot></slot>
        </template>

        <!-- 自定义底部 -->
        <template #footer v-if="$slots.footer">
            <slot name="footer"></slot>
        </template>
    </van-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { DialogTheme, DialogMessageAlign } from 'vant'

// 定义 beforeClose 回调函数类型
type DialogBeforeClose = (action: string) => boolean | Promise<boolean>

interface Props {
    // 显示状态
    show?: boolean
    // 标题
    title?: string
    // 宽度
    width?: number | string
    // 消息内容
    message?: string
    // 消息对齐方式
    messageAlign?: DialogMessageAlign
    // 主题样式
    theme?: DialogTheme
    // 自定义类名
    className?: string
    // 是否显示取消按钮
    showCancelButton?: boolean
    // 是否显示确认按钮
    showConfirmButton?: boolean
    // 取消按钮文字
    cancelButtonText?: string
    // 确认按钮文字
    confirmButtonText?: string
    // 取消按钮颜色
    cancelButtonColor?: string
    // 确认按钮颜色
    confirmButtonColor?: string
    // 是否显示遮罩层
    overlay?: boolean
    // 自定义遮罩层类名
    overlayClass?: string
    // 自定义遮罩层样式
    overlayStyle?: Record<string, any>
    // 是否在点击遮罩层后关闭弹窗
    closeOnClickOverlay?: boolean
    // 是否在页面回退时自动关闭
    closeOnPopstate?: boolean
    // 是否开启延迟渲染
    lazyRender?: boolean
    // 是否锁定背景滚动
    lockScroll?: boolean
    // 是否允许消息内容中渲染HTML
    allowHtml?: boolean
    // 关闭前的回调函数
    beforeClose?: DialogBeforeClose
}

const props = withDefaults(defineProps<Props>(), {
    show: false,
    title: '',
    width: '320px',
    message: '',
    messageAlign: 'center',
    theme: 'default',
    showCancelButton: false,
    showConfirmButton: true,
    cancelButtonText: 'Cancel',
    confirmButtonText: 'Confirm',
    overlay: true,
    closeOnClickOverlay: false,
    closeOnPopstate: true,
    lazyRender: true,
    lockScroll: true,
    allowHtml: false
})

interface Emits {
    'update:show': [value: boolean]
    'confirm': []
    'cancel': []
    'open': []
    'close': []
    'opened': []
    'closed': []
}

const emit = defineEmits<Emits>()

// 双向绑定显示状态
const visible = computed({
    get: () => props.show,
    set: (value) => emit('update:show', value)
})

// 事件处理
const handleConfirm = () => {
    emit('confirm')
}

const handleCancel = () => {
    emit('cancel')
}

const handleOpen = () => {
    emit('open')
}

const handleClose = () => {
    emit('close')
}

const handleOpened = () => {
    emit('opened')
}

const handleClosed = () => {
    emit('closed')
}

const handleBeforeClose = (action: string) => {
    if (props.beforeClose) {
        return props.beforeClose(action)
    }
    return true
}
</script>

<style lang="scss" scoped>
// 确保对话框能够正确显示
:deep(.van-dialog) {
    background: linear-gradient(180deg, #2A2D47 0%, #1A1D35 100%) !important;
    border-radius: 16px;
    z-index: 10000 !important;
    position: fixed !important;
    color: #FFFFFF !important;
}

:deep(.van-overlay) {
    z-index: 9999 !important;
    position: fixed !important;
    background-color: rgba(0, 0, 0, 0.7) !important;
}

:deep(.van-dialog__header) {
    padding-top: 24px;
    padding-bottom: 16px;
    color: #FFFFFF !important;
}

:deep(.van-dialog__title) {
    color: #FFFFFF !important;
    font-weight: 700;
}

:deep(.van-dialog__message) {
    padding: 16px 24px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.8) !important;
}

:deep(.van-dialog__footer) {
    padding: 16px 24px 24px;
    gap: 12px;
}

:deep(.van-button) {
    border-radius: 12px;
    height: 44px;
    font-weight: 600;
}

:deep(.van-button--default) {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: #FFFFFF !important;
    border: none !important;
}

:deep(.van-button--primary) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    color: #FFFFFF !important;
    border: none !important;

}
:deep(.van-hairline--left:after){
    border: none !important;
}
</style>
